import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import axios from 'axios';
import { Code2, MoveLeft, ArrowRight, XIcon } from 'lucide-react';
import host from '../../../global';
import { useTheme } from '../../../hooks/useTheme';
import Loader from '../../Loader';

const getInterviewIcon = (roundType) => {
  switch (roundType) {
    case 'Technical Interview':
      return <img src="/technical_type.png" className="w-8 h-8" alt="Technical" />;
    case 'Knowledge Test':
    case 'MCQ Test':
      return <img src="/mcq_type.png" className="w-8 h-8" alt="MCQ" />;
    case 'Coding Test':
      return <img src="/coding_type.png" className="w-8 h-8" alt="Coding" />;
    default:
      return <Code2 className="w-6 h-6 text-[#C9A3FF]" />;
  }
};

const SharedJDOverviewPage = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const accessToken = sessionStorage.getItem('accessToken');
  const { theme } = useTheme();

  const jdId = location.state?.jd_id || null;
  const jobDescriptionType = location.state?.jobDescriptionType || null;

  const [formData, setFormData] = useState({});
  const [isLoading, setIsLoading] = useState(true);
  const [isShowJDView, setShowJDView] = useState(false);
  const jdModalRef = useRef(null);

  useEffect(() => {
    const handleClickOutside = (event) => {
      if (jdModalRef.current && !jdModalRef.current.contains(event.target)) {
        setShowJDView(false);
      }
    };
    if (isShowJDView) document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isShowJDView]);

  useEffect(() => {
    if (!jdId) {
      navigate('/org-admin/created-jds');
      return;
    }

    const fetchJD = async () => {
      try {
        const response = await axios.get(`${host}/get_jd_for_edit/${jdId}`, {
          headers: { Authorization: `Bearer ${accessToken}` },
        });
        const data = response.data;
        const description = data.description || {};

        const sequence = (data.assessment_sequence || []).map((id) => {
          switch (id) {
            case 'interview':  return { id: 'interview',  label: 'Technical Interview', mandatory: true };
            case 'mcq':        return { id: 'mcq',        label: 'Knowledge Test',      mandatory: false };
            case 'coding':     return { id: 'coding',     label: 'Coding Test',         mandatory: false };
            case 'typing':     return { id: 'typing',     label: 'Typing Test',         mandatory: false };
            case 'behavioral': return { id: 'behavioral', label: 'Behavioural Assessment', mandatory: false };
            default: return null;
          }
        }).filter(Boolean);

        setFormData({
          jd_id: data._id,
          jobRole: data.jobrole || '',
          minExp: description.minExp ?? 0,
          maxExp: description.maxExp ?? 1,
          responsibilities: description.Raw_Job_Description || '',
          generatedJD: {
            ...description,
            job_description: description.Raw_Job_Description || '',
          },
          sequence,
        });
      } catch (err) {
        navigate('/org-admin/created-jds');
      } finally {
        setIsLoading(false);
      }
    };

    fetchJD();
  }, [jdId, accessToken]); // eslint-disable-line

  if (isLoading) {
    return (
      <div className="hr-profiles flex items-center justify-center py-20">
        <Loader />
      </div>
    );
  }

  const handleEditRound = () => {
    if (jobDescriptionType === 'customized') {
      navigate(`/org-admin/create-jd-custom?id=${jdId}`);
    } else {
      navigate('/org-admin/create-jd-smart', {
        state: { jd_id: jdId, mode: 'edit' },
      });
    }
  };

  const jdText = formData.generatedJD?.job_description || formData.responsibilities;
  const rolesText = formData.generatedJD?.['Roles & Responsibilities']?.length
    ? `Roles & Responsibilities:\n${formData.generatedJD['Roles & Responsibilities'].map((item, i) => `${i + 1}. ${item}`).join('\n')}`
    : '';

  return (
    <div className="min-h-screen dark:bg-none bg-bgSecondary flex flex-col border border-gray-700/50 rounded-2xl pt-2">
      <div className="flex-1 w-full px-4" style={{ transform: 'translateZ(0)', contain: 'layout' }}>
        <div className="px-12 mx-auto">

          {/* Header */}
          <div className="flex items-center pb-6 border-b border-gray-700/50 mt-6 space-x-6">
            <button onClick={() => navigate('/org-admin/created-jds')}>
              <MoveLeft className="w-6 h-6 dark:text-[#C9A3FF] text-textSecondary" />
            </button>
            <p className="dark:text-white text-textPrimary font-semibold text-lg">
              {formData.jobRole || <span className="text-gray-500 font-normal text-base">Not specified</span>}
            </p>
            <div className="h-12 w-px dark:bg-[#CCCCCC] bg-gray-400" />
            <div className="space-y-2">
              <label className="block text-sm font-medium dark:text-gray-400 text-textSecondary">
                Experience Level
              </label>
              <p className="dark:text-white text-textSecondary font-semibold text-lg">
                {`${formData.minExp}-${formData.maxExp} Years`}
              </p>
            </div>
            <div className="h-12 w-px dark:bg-[#CCCCCC] bg-gray-400" />
            <button className="button" onClick={() => setShowJDView(true)}>
              <span className="px-2">View Job Description</span>
            </button>
          </div>

          {/* Interview Rounds */}
          <div className="flex flex-col mt-8 space-y-2">
            <span className="dark:text-[#CCCCCC] text-textSecondary">Interview Rounds</span>
          </div>

          <div className="mt-4">
            <div className="flex items-center gap-4 overflow-x-auto pb-4 scrollbar-thin scrollbar-thumb-gray-700 scrollbar-track-transparent">
              {formData.sequence?.map((round, index) => (
                <React.Fragment key={round.id || index}>
                  <div
                    className="transition-all duration-200 ease-in-out flex-shrink-0 rounded-xl border border-gray-700/50 p-4 relative flex flex-col items-center justify-center min-w-[200px] max-w-[250px] dark:hover:shadow-lg hover:shadow-sm hover:border-purple-400 dark:hover:bg-purple-500/20 hover:bg-gray-100"
                    style={{ backgroundImage: 'linear-gradient(233.57deg, rgba(217, 217, 217, 0.05) -5.01%, rgba(115, 108, 207, 0.01) 100%)' }}
                  >
                    <div className="flex items-center justify-between mb-4">
                      <div className="flex flex-col items-center gap-1.5 relative">
                        <div className="mt-2">{getInterviewIcon(round.label)}</div>
                        <span className="dark:text-[#C9A3FF] text-textSecondary first-line:text-lg text-center">
                          {round.label}
                        </span>
                        <span className="dark:text-[#C9A3FF] text-textSecondary font-light text-sm">
                          {`Round ${index + 1}`}
                        </span>
                      </div>
                    </div>
                    <button
                      onClick={handleEditRound}
                      className={`${theme === 'dark' && 'secondary-button px-3 py-2 pr-3.5 !text-white'} border-btn !text-textSecondary !px-3 !py-2 !pr-3.5 w-full`}
                    >
                      Edit Round
                    </button>
                  </div>

                  {index < formData.sequence.length - 1 && (
                    <div className="flex-shrink-0 flex items-center">
                      <ArrowRight className="w-5 h-5 text-gray-400" />
                    </div>
                  )}
                </React.Fragment>
              ))}
            </div>
          </div>

        </div>
      </div>

      {/* JD modal */}
      {isShowJDView && (
        <div className="fixed inset-0 flex items-center justify-center bg-opacity-5 z-[700] p-4">
          <div
            ref={jdModalRef}
            className="flex flex-col dark:glass-effect bg-bgSecondary rounded-lg shadow-lg backdrop-blur-[500px] w-full max-w-3xl h-[70vh] relative"
          >
            <div className="flex justify-between items-start p-6 pb-4 gap-4 flex-shrink-0">
              <div className="flex flex-col">
                <span className="text-xs tracking-wide text-textSecondary dark:text-gray-400">Job Role</span>
                <h2 className="text-lg font-semibold text-textPrimary dark:text-white mt-1">
                  {formData.jobRole || 'Job Description'}
                </h2>
                {formData.minExp != null && formData.maxExp != null && (
                  <span className="text-sm text-textSecondary dark:text-gray-400">
                    {formData.minExp} - {formData.maxExp} yrs
                  </span>
                )}
              </div>
              <button onClick={() => setShowJDView(false)} className="hover:text-opacity-80 dark:text-white text-textPrimary">
                <XIcon className="w-5 h-5" />
              </button>
            </div>
            <div className="px-6 pb-6 space-y-6 overflow-y-auto overflow-x-hidden flex-1">
              {jdText ? (
                <pre className="font-sans dark:glass-effect bg-bgPrimary text-md whitespace-pre-wrap break-words p-6 rounded-2xl shadow-inner dark:text-white text-textPrimary">
                  {rolesText ? `${jdText}\n\n${rolesText}` : jdText}
                </pre>
              ) : (
                <p className="dark:text-gray-400 text-textSecondary">No job description available</p>
              )}
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SharedJDOverviewPage;
